import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../services/server";
import { useAuth } from "../services/AuthContext";
import Main from "./Main";
import DataRoom, { QuestionsPanel } from "./dataroom";
import { makeSellerNav } from "./nav";
import RiskProfileQuestionnaireForm from "./components/RiskProfileQuestionnaireForm";
import OwnerQuestionnaireForm from "./components/OwnerQuestionnaireForm";
import DueDiligenceChecklist from "./components/DueDiligenceChecklist";
import "./css/seller-portal.css";
import "./css/dataroom.css";

const DD_FORM_INFO = {
  risk_profile: {
    label: "Riskprofil",
    description: "Bedömning av kundkoncentration, nyckelpersoner, avtal, finansiell hälsa och compliance.",
  },
  owner_questionnaire: {
    label: "Ägarformulär",
    description: "Ägarbild, säljmotiv, dealstruktur, organisation och historiska justeringar.",
  },
  ddq_checklist: {
    label: "DD-checklista",
    description: "Strukturerad lista över dokument och bilagor som behövs för köparens due diligence.",
  },
};

async function apiFetch(path, options = {}) {
  const csrf = getCsrfToken();
  const headers = { Accept: "application/json", ...(options.headers || {}) };
  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (csrf && !["GET", "HEAD"].includes((options.method || "GET").toUpperCase())) {
    headers["X-CSRFToken"] = csrf;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers,
    body:
      options.body instanceof FormData
        ? options.body
        : options.body
        ? JSON.stringify(options.body)
        : undefined,
  });
  return res;
}

// ─── Financials module ──────────────────────────────────────────────────────

function FinancialsModule({ workspaceKey }) {
  const [files, setFiles] = useState([]);
  const [hasSie, setHasSie] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef();

  // Check whether the broker (or anyone) has already uploaded a SIE file —
  // if so we suppress the upload prompt so the seller isn't asked twice.
  useEffect(() => {
    if (!workspaceKey) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/processing/workspace/${workspaceKey}/sie/upload/`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setHasSie(!!data.has_sie);
        setFiles(Array.isArray(data.files) ? data.files : []);
      } catch (e) {
        console.error("SIE status fetch failed", e);
      } finally {
        if (!cancelled) setStatusLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceKey]);

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setMessage("");
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await apiFetch(`/processing/workspace/${workspaceKey}/sie/upload/`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail || "Upload failed.";
        const reason = data.error ? ` (${data.error})` : "";
        throw new Error(detail + reason);
      }
      setMessage("File uploaded successfully.");
      setFiles((prev) => [{ name: file.name, uploaded_at: new Date().toISOString() }, ...prev]);
      setHasSie(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  if (statusLoaded && hasSie) {
    return (
      <section className="sp-module">
        <h2 className="sp-module-title">Finansiell data</h2>
        <p className="sp-module-desc">
          SIE-filen är redan uppladdad. Du behöver inte göra något här —
          mäklaren har tillgång till underlaget och en preliminär värdering
          har räknats ut.
        </p>
        {files.length > 0 && (
          <ul className="sp-file-list">
            {files.map((f, i) => (
              <li key={i} className="sp-file-item">
                ✓ {f.name}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Finansiell data</h2>
      <p className="sp-module-desc">
        Ladda upp en SIE-fil (.se / .sie) exporterad från ditt bokföringssystem.
        Filen tolkas automatiskt och används som underlag för en preliminär
        värdering.
      </p>

      <label className="sp-upload-btn">
        {uploading ? "Laddar upp…" : "Välj SIE-fil"}
        <input
          ref={inputRef}
          type="file"
          accept=".se,.sie"
          onChange={handleUpload}
          disabled={uploading}
          style={{ display: "none" }}
        />
      </label>

      {message && <p className="sp-success">{message}</p>}
      {error && <p className="sp-error">{error}</p>}

      {files.length > 0 && (
        <ul className="sp-file-list">
          {files.map((f, i) => (
            <li key={i} className="sp-file-item">
              {f.name}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── DD module ──────────────────────────────────────────────────────────────

const DD_QUESTIONS = [
  { key: "legal_disputes", label: "Are there any ongoing legal disputes?" },
  { key: "environmental_issues", label: "Are there any known environmental issues?" },
  { key: "customer_concentration", label: "Does any single customer account for more than 30% of revenue?" },
  { key: "key_person_dependency", label: "Is the business highly dependent on one or two key people?" },
  { key: "pending_contracts", label: "Are there any major contracts up for renewal in the next 12 months?" },
  { key: "outstanding_debt", label: "Are there any outstanding debts or liabilities not on the balance sheet?" },
];

function DDModule({ workspaceKey, workspaceOrgnr }) {
  const [tab, setTab] = useState("risk");
  const [showDelegate, setShowDelegate] = useState(false);
  const [delegations, setDelegations] = useState([]);
  const [delegationsLoading, setDelegationsLoading] = useState(false);

  const orgnr = workspaceOrgnr || workspaceKey;
  const quizzesUrl = `/users/seller-portal/${workspaceKey}/quizzes/`;
  const delegationsUrl = `/users/seller-portal/${workspaceKey}/dd-delegations/`;

  const loadDelegations = useCallback(async () => {
    setDelegationsLoading(true);
    try {
      const res = await apiFetch(delegationsUrl);
      if (res.ok) {
        const data = await res.json().catch(() => []);
        setDelegations(Array.isArray(data) ? data : []);
      }
    } finally {
      setDelegationsLoading(false);
    }
  }, [delegationsUrl]);

  useEffect(() => { loadDelegations(); }, [loadDelegations]);

  async function handleRevoke(id) {
    if (!window.confirm("Återkalla denna delegering?")) return;
    const res = await apiFetch(`${delegationsUrl}${id}/`, { method: "DELETE" });
    if (res.ok) loadDelegations();
  }

  return (
    <section className="sp-module">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <h2 className="sp-module-title">Due diligence</h2>
          <p className="sp-module-desc" style={{ marginTop: 4 }}>
            Fyll i de tre formulären nedan. Du kan delegera enskilda delar till en kollega.
          </p>
        </div>
        <button
          type="button"
          className="sp-save-btn"
          onClick={() => setShowDelegate(true)}
          style={{ marginTop: 0 }}
        >
          + Delegera
        </button>
      </div>

      {/* Tabs */}
      <div className="sp-dd-tabs" role="tablist">
        {[
          { id: "risk",  label: "Riskprofil" },
          { id: "owner", label: "Ägarformulär" },
          { id: "ddq",   label: "DD-checklista" },
        ].map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={"sp-dd-tab" + (tab === t.id ? " sp-dd-tab--active" : "")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="sp-dd-tab-body">
        {tab === "risk" && (
          <RiskProfileQuestionnaireForm orgnr={orgnr} quizzesUrl={quizzesUrl} />
        )}
        {tab === "owner" && (
          <OwnerQuestionnaireForm orgnr={orgnr} quizzesUrl={quizzesUrl} />
        )}
        {tab === "ddq" && (
          <DueDiligenceChecklist
            company={orgnr}
            storageKey={`seller-ddq-${workspaceKey}`}
            quizzesUrl={quizzesUrl}
          />
        )}
      </div>

      {/* Active delegations */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #E7E5E4" }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: "#1C1917", margin: "0 0 8px" }}>
          Aktiva delegeringar
        </h3>
        {delegationsLoading && <p className="sp-module-desc">Laddar…</p>}
        {!delegationsLoading && delegations.length === 0 && (
          <p className="sp-module-desc">Inga aktiva delegeringar.</p>
        )}
        {!delegationsLoading && delegations.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {delegations.filter((d) => d.status !== "revoked").map((d) => (
              <li
                key={d.id}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", border: "1px solid #E7E5E4", borderRadius: 8,
                  marginBottom: 8, background: "#FAFAF9",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: "#1C1917" }}>
                    {d.recipient_name ? `${d.recipient_name} (${d.email})` : d.email}
                  </div>
                  <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
                    {[
                      d.allows_risk_profile && "Riskprofil",
                      d.allows_owner_questionnaire && "Ägarformulär",
                      d.allows_ddq_checklist && "DD-checklista",
                    ].filter(Boolean).join(" · ")}
                    {" · "}<span style={{ textTransform: "capitalize" }}>{d.status}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevoke(d.id)}
                  style={{
                    background: "none", border: "1px solid #E7E5E4",
                    padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                    fontSize: 12, color: "#78716C",
                  }}
                >
                  Återkalla
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showDelegate && (
        <DelegateModal
          delegationsUrl={delegationsUrl}
          onClose={() => setShowDelegate(false)}
          onCreated={() => { setShowDelegate(false); loadDelegations(); }}
        />
      )}
    </section>
  );
}

function DelegateModal({ delegationsUrl, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [allows, setAllows] = useState({
    allows_risk_profile: false,
    allows_owner_questionnaire: false,
    allows_ddq_checklist: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const anyChecked = Object.values(allows).some(Boolean);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !anyChecked) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await apiFetch(delegationsUrl, {
        method: "POST",
        body: { email: email.trim(), recipient_name: recipientName.trim(), ...allows },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunde inte skapa delegering.");
      }
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: 24,
          width: "min(520px, 92vw)", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#1C1917" }}>
          Delegera due diligence
        </h2>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#78716C" }}>
          Bjud in en kollega via e-post. Välj vilka delar de ska få fylla i.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917", marginBottom: 4 }}>
              E-post *
            </div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px", border: "1px solid #D6D3D1",
                borderRadius: 6, fontSize: 14, boxSizing: "border-box",
              }}
            />
          </label>

          <label style={{ display: "block", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917", marginBottom: 4 }}>
              Namn (valfritt)
            </div>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              style={{
                width: "100%", padding: "8px 12px", border: "1px solid #D6D3D1",
                borderRadius: 6, fontSize: 14, boxSizing: "border-box",
              }}
            />
          </label>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1917", marginBottom: 8 }}>
              Delar att delegera *
            </div>
            {Object.entries(DD_FORM_INFO).map(([key, info]) => {
              const flagKey =
                key === "risk_profile" ? "allows_risk_profile" :
                key === "owner_questionnaire" ? "allows_owner_questionnaire" :
                "allows_ddq_checklist";
              return (
                <label
                  key={key}
                  style={{
                    display: "flex", gap: 10, padding: "10px 12px", marginBottom: 6,
                    border: "1px solid #E7E5E4", borderRadius: 8, cursor: "pointer",
                    background: allows[flagKey] ? "#F5F7EF" : "#FAFAF9",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allows[flagKey]}
                    onChange={(e) => setAllows((a) => ({ ...a, [flagKey]: e.target.checked }))}
                    style={{ marginTop: 3 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: "#1C1917", fontSize: 14 }}>{info.label}</div>
                    <div style={{ fontSize: 12, color: "#78716C", marginTop: 2, lineHeight: 1.4 }}>
                      {info.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {error && <p className="sp-error" style={{ marginBottom: 12 }}>{error}</p>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none", border: "1px solid #D6D3D1", padding: "8px 16px",
                borderRadius: 6, cursor: "pointer", color: "#57534E", fontSize: 14,
              }}
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting || !email.trim() || !anyChecked}
              className="sp-save-btn"
              style={{ marginTop: 0 }}
            >
              {submitting ? "Skickar…" : "Skicka inbjudan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Company details module ─────────────────────────────────────────────────

function CompanyDetailsModule({ workspaceKey, sellerCompanyName }) {
  const [description, setDescription] = useState("");
  const [teamInfo, setTeamInfo] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const logoRef = useRef();

  useEffect(() => {
    let alive = true;
    apiFetch(`/users/seller-portal/${workspaceKey}/company-details/`)
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        setDescription(data?.description || "");
        setTeamInfo(data?.team_info || "");
        if (data?.logo_url) setLogoPreview(data.logo_url);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [workspaceKey]);

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const form = new FormData();
      form.append("description", description);
      form.append("team_info", teamInfo);
      if (logoFile) form.append("logo", logoFile);

      const res = await apiFetch(`/users/seller-portal/${workspaceKey}/company-details/`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunde inte spara.");
      }
      setMessage("Sparat.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Bolagsinfo</h2>
      <p className="sp-module-desc">
        Fyll i information om {sellerCompanyName} som kommer att användas i
        informationsmemorandumet.
      </p>

      <div className="sp-field">
        <label className="sp-label">Logotyp</label>
        {logoPreview && (
          <img src={logoPreview} alt="Förhandsvisning av logotyp" className="sp-logo-preview" />
        )}
        <label className="sp-upload-btn" style={{ display: "inline-block", marginTop: 8 }}>
          {logoFile ? logoFile.name : "Välj logotyp"}
          <input
            ref={logoRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ display: "none" }}
          />
        </label>
      </div>

      <div className="sp-field">
        <label className="sp-label">Bolagsbeskrivning</label>
        <textarea
          className="sp-textarea"
          rows={5}
          placeholder="Beskriv bolaget, dess produkter eller tjänster och marknadsposition…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="sp-field">
        <label className="sp-label">Nyckelpersoner</label>
        <textarea
          className="sp-textarea"
          rows={4}
          placeholder="Lista nyckelpersoner och deras roller…"
          value={teamInfo}
          onChange={(e) => setTeamInfo(e.target.value)}
        />
      </div>

      {message && <p className="sp-success">{message}</p>}
      {error && <p className="sp-error">{error}</p>}

      <button className="sp-save-btn" onClick={handleSave} disabled={saving}>
        {saving ? "Sparar…" : "Spara"}
      </button>
    </section>
  );
}

// ─── NDA gate ─────────────────────────────────────────────────────────────────

// Phase 1: show NDA text, user scrolls to bottom and clicks "Proceed to sign"
// Phase 2: show DocuSeal iframe for e-signing
// Phase 3: poll status until "signed", then call onSigned()

function NDAGate({ workspaceKey, onSigned }) {
  const [phase,       setPhase]       = useState("loading"); // loading | preview | signing | done | error
  const [nda,         setNda]         = useState(null);
  const [scrolled,    setScrolled]    = useState(false);
  const [signingUrl,  setSigningUrl]  = useState(null);
  const [error,       setError]       = useState("");
  const pollRef = useRef(null);

  // ── Load NDA text ──────────────────────────────────────────────────────────
  useEffect(() => {
    apiFetch(`/users/workspaces/${workspaceKey}/nda/sign/`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Could not load NDA.");
        return data;
      })
      .then((data) => {
        if (!data.requires_nda || data.already_signed) {
          onSigned();
        } else {
          setNda(data);
          setPhase("preview");
        }
      })
      .catch((err) => { setError(err.message); setPhase("error"); });
  }, [workspaceKey, onSigned]);

  // ── Scroll tracker ────────────────────────────────────────────────────────
  function handleScroll(e) {
    const el = e.currentTarget;
    if (!scrolled && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setScrolled(true);
    }
  }

  // ── Initiate signing ──────────────────────────────────────────────────────
  async function handleProceed() {
    setPhase("initiating");
    setError("");
    try {
      const res = await apiFetch(`/signing/${workspaceKey}/initiate/`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not initiate signing.");
      if (data.already_signed) { onSigned(); return; }
      setSigningUrl(data.signing_url);
      setPhase("signing");
      startPolling();
    } catch (err) {
      setError(err.message);
      setPhase("preview");
    }
  }

  // ── Poll for completion ───────────────────────────────────────────────────
  function startPolling() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/signing/${workspaceKey}/status/`);
        const data = await res.json().catch(() => ({}));
        if (data.status === "signed") {
          stopPolling();
          onSigned();
        }
      } catch (_) { /* ignore transient errors */ }
    }, 3000);
  }

  function stopPolling() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  // Also listen for DocuSeal postMessage completion event
  useEffect(() => {
    function onMessage(e) {
      if (e.data && e.data.type === "docuseal:completed") {
        stopPolling();
        onSigned();
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      stopPolling();
    };
  }, [onSigned]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (phase === "loading" || phase === "initiating") {
    return (
      <div className="sp-shell">
        <div className="sp-loading">{phase === "initiating" ? "Initierar signering…" : "Laddar…"}</div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="sp-shell">
        <div className="sp-error" style={{ padding: "32px", textAlign: "center" }}>{error}</div>
      </div>
    );
  }

  if (phase === "signing" && signingUrl) {
    return (
      <div className="sp-shell">
        <div className="nda-gate" style={{ maxWidth: 860 }}>
          <div className="nda-gate-header">
            {nda?.logo_data && <img src={nda.logo_data} alt="Logo" className="nda-gate-logo" />}
            <h1 className="nda-gate-title">{nda?.title}</h1>
            <p className="nda-gate-hint">Signera dokumentet i formuläret nedan.</p>
          </div>
          {error && <p className="sp-error" style={{ textAlign: "center", marginBottom: 8 }}>{error}</p>}
          <iframe
            src={signingUrl}
            title="Signera NDA"
            style={{
              width: "100%",
              height: 620,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
            }}
            allow="camera"
          />
          <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 8 }}>
            Sidan uppdateras automatiskt när du har signerat.
          </p>
        </div>
      </div>
    );
  }

  // phase === "preview"
  return (
    <div className="sp-shell">
      <div className="nda-gate">
        <div className="nda-gate-header">
          {nda?.logo_data && (
            <img src={nda.logo_data} alt="Logo" className="nda-gate-logo" />
          )}
          <h1 className="nda-gate-title">{nda?.title}</h1>
          <p className="nda-gate-hint">Läs igenom dokumentet och scrolla till botten för att fortsätta.</p>
        </div>

        <div className="nda-gate-body" onScroll={handleScroll}>
          <pre className="nda-gate-content">{nda?.content}</pre>
        </div>

        <div className="nda-gate-footer">
          {error && <p className="sp-error" style={{ marginBottom: 12 }}>{error}</p>}

          {!scrolled && (
            <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 0, marginBottom: 12 }}>
              Scrolla till slutet av dokumentet för att fortsätta.
            </p>
          )}

          <button
            className="sp-save-btn"
            style={{ width: "100%" }}
            onClick={handleProceed}
            disabled={!scrolled}
          >
            Fortsätt till signering
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main portal ─────────────────────────────────────────────────────────────

/* ─────────────────────────────────────────────────────────
   Layout + per-module pages
───────────────────────────────────────────────────────── */

// Ordered guided flow for the simplified seller experience. Each step
// references a sub-route under /seller/<slug>/. `module` is the grant key
// from `info.modules` — if the broker didn't grant the module the step is
// dropped from the flow (the seller never gets stuck on a section they
// can't access). The valuation/dataroom/team modules are kept out of the
// wizard since they are reference/read-only rather than data-entry steps.
const SIMPLE_FLOW_STEPS = [
  { key: "company",     path: "company",     label: "Bolagsinfo",       module: "company_details" },
  { key: "financials",  path: "financials",  label: "Finansiell data",  module: "financials" },
  { key: "dd",          path: "dd",          label: "Due diligence",    module: "dd" },
];

export default function SellerPortalLayout() {
  const { company } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  // Flow mode lives on OrganisationWorkspaceUser.flow_mode so the choice
  // follows the seller across devices. Hydrated from `info.flow_mode`
  // once the portal info has loaded; defaults to "advanced" otherwise so
  // sellers who already had a bookmarked non-wizard page (e.g.
  // /seller/<slug>/team) don't get bounced back to step 1 of the
  // wizard.
  const [flowMode, setFlowModeState] = useState("advanced");

  // Persist flow-mode changes to the backend optimistically — UI flips
  // immediately, then PATCH catches up. Falls back gracefully if the
  // server rejects so the seller never gets stuck.
  const persistFlowMode = useCallback(
    async (next) => {
      try {
        await apiFetch(`/users/seller-portal/${company}/`, {
          method: "PATCH",
          body: JSON.stringify({ flow_mode: next }),
        });
      } catch (e) {
        console.warn("Failed to persist flow_mode:", e);
      }
    },
    [company],
  );

  const setFlowMode = useCallback(
    (next) => {
      setFlowModeState(next);
      persistFlowMode(next);
    },
    [persistFlowMode],
  );

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch {
      setLoggingOut(false);
    }
  }

  const [info,     setInfo]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [ndaDone,  setNdaDone]  = useState(false);

  const fetchInfo = useCallback(() => {
    setLoading(true);
    apiFetch(`/users/seller-portal/${company}/`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Could not load workspace.");
        return data;
      })
      .then((data) => {
        setInfo(data);
        if (!data.nda_required || data.nda_signed) setNdaDone(true);
        if (data.flow_mode === "simple" || data.flow_mode === "advanced") {
          setFlowModeState(data.flow_mode);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [company]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleNdaSigned = useCallback(() => {
    setNdaDone(true);
    fetchInfo();
  }, [fetchInfo]);

  // Steps available to THIS seller — drop entries the broker hasn't
  // granted as a module, so the wizard never shows a section the seller
  // can't access.
  const activeSteps = useMemo(() => {
    const granted = new Set(info?.modules || []);
    return SIMPLE_FLOW_STEPS.filter((s) => granted.has(s.module));
  }, [info?.modules]);

  const currentStepIndex = useMemo(() => {
    if (flowMode !== "simple" || activeSteps.length === 0) return -1;
    const tail = location.pathname.split("/").filter(Boolean).pop() || "";
    const idx = activeSteps.findIndex((s) => s.path === tail);
    return idx >= 0 ? idx : 0;
  }, [flowMode, activeSteps, location.pathname]);

  // When simple mode is active and the seller lands on a non-step route
  // (e.g. /dataroom from the default redirect), bounce them to the first
  // wizard step. Keep them in advanced mode if they explicitly toggled.
  useEffect(() => {
    if (flowMode !== "simple" || activeSteps.length === 0) return;
    const tail = location.pathname.split("/").filter(Boolean).pop() || "";
    const inFlow = activeSteps.some((s) => s.path === tail);
    if (!inFlow) {
      navigate(`/seller/${company}/${activeSteps[0].path}`, { replace: true });
    }
  }, [flowMode, activeSteps, location.pathname, company, navigate]);

  const navItems = React.useMemo(
    () => (flowMode === "simple" ? [] : makeSellerNav(company, info?.modules || [])),
    [flowMode, company, info?.modules],
  );

  function handleNextStep() {
    if (currentStepIndex < 0) return;
    // Refetch step_status so the next progress bar reflects what the
    // seller just filled in on the current step.
    fetchInfo();
    const isLast = currentStepIndex === activeSteps.length - 1;
    if (isLast) {
      // Wizard complete — drop them onto the dataroom (read-only summary).
      navigate(`/seller/${company}/dataroom`, { replace: false });
      setFlowMode("advanced");
      return;
    }
    navigate(`/seller/${company}/${activeSteps[currentStepIndex + 1].path}`);
  }

  function handlePrevStep() {
    if (currentStepIndex <= 0) return;
    navigate(`/seller/${company}/${activeSteps[currentStepIndex - 1].path}`);
  }

  if (loading) {
    return (
      <Main navItems={navItems}>
        <div className="sp-shell"><div className="sp-loading">Loading…</div></div>
      </Main>
    );
  }

  if (error) {
    return (
      <Main navItems={navItems}>
        <div className="sp-shell"><div className="sp-error">{error}</div></div>
      </Main>
    );
  }

  // NDA gate — applies to every seller-side user (including invited colleagues).
  if (info?.nda_required && !ndaDone) {
    return <NDAGate workspaceKey={company} onSigned={handleNdaSigned} />;
  }

  const { workspace_name, seller_company_name } = info || {};
  const email = user?.user?.email || user?.user?.username || user?.email || user?.username || "";

  const isSimple = flowMode === "simple" && activeSteps.length > 0;
  const isLastStep = isSimple && currentStepIndex === activeSteps.length - 1;
  const currentStep = isSimple ? activeSteps[currentStepIndex] : null;

  return (
    <Main navItems={navItems}>
      <div className="sp-shell">
        <header
          className="sp-header"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}
        >
          <div>
            <div className="sp-header-title">{workspace_name}</div>
            <div className="sp-header-sub">{seller_company_name}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <FlowModeToggle value={flowMode} onChange={setFlowMode} />
            <div style={{ textAlign: "right", fontSize: 13, color: "#6b7280" }}>
              <div style={{ marginBottom: 6 }}>{email}</div>
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                style={{
                  background: "transparent",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 12,
                  cursor: loggingOut ? "not-allowed" : "pointer",
                  color: "#374151",
                }}
              >
                {loggingOut ? "Loggar ut…" : "Logga ut"}
              </button>
            </div>
          </div>
        </header>

        {isSimple && (
          <WizardProgress
            steps={activeSteps}
            currentIndex={currentStepIndex}
            currentLabel={currentStep?.label}
            stepStatus={info?.step_status || {}}
          />
        )}

        <main className="sp-main">
          <Outlet context={{ workspaceKey: company, info, sellerCompanyName: seller_company_name }} />
        </main>

        {isSimple && (
          <WizardFooter
            currentIndex={currentStepIndex}
            totalSteps={activeSteps.length}
            isLast={isLastStep}
            onBack={handlePrevStep}
            onNext={handleNextStep}
          />
        )}
      </div>
    </Main>
  );
}

function FlowModeToggle({ value, onChange }) {
  const base = {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#6b7280",
    transition: "all 0.15s",
  };
  const active = {
    ...base,
    background: "#1c1917",
    color: "#fff",
    borderColor: "#1c1917",
  };
  return (
    <div
      role="tablist"
      aria-label="Flödesläge"
      style={{ display: "inline-flex", borderRadius: 999, overflow: "hidden", border: "1px solid #d1d5db" }}
    >
      <button
        type="button"
        role="tab"
        aria-selected={value === "simple"}
        onClick={() => onChange("simple")}
        style={{ ...(value === "simple" ? active : base), borderRadius: 0, border: "none" }}
      >
        Vägled mig
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "advanced"}
        onClick={() => onChange("advanced")}
        style={{ ...(value === "advanced" ? active : base), borderRadius: 0, border: "none" }}
      >
        Avancerat
      </button>
    </div>
  );
}

function WizardProgress({ steps, currentIndex, currentLabel, stepStatus }) {
  // Completion-based progress — count steps with stepStatus[key] === true,
  // not just position. Falls back to position-based if status missing.
  const completedCount = steps.filter((s) => stepStatus?.[s.key]).length;
  const positionPct = steps.length > 0 ? ((currentIndex + 1) / steps.length) * 100 : 0;
  const completedPct = steps.length > 0 ? (completedCount / steps.length) * 100 : 0;
  return (
    <div
      style={{
        padding: "16px 24px",
        background: "#fafaf9",
        borderBottom: "1px solid #e7e5e4",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1c1917" }}>{currentLabel}</div>
        <div style={{ fontSize: 12, color: "#78716c" }}>
          Steg {currentIndex + 1} av {steps.length} · {completedCount} klara
        </div>
      </div>
      <div style={{ position: "relative", height: 4, background: "#e7e5e4", borderRadius: 2, overflow: "hidden", marginBottom: 12 }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${completedPct}%`,
            background: "#16a34a",
            transition: "width 0.3s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${positionPct}%`,
            background: "transparent",
            borderRight: "2px solid #1c1917",
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
        {steps.map((s, idx) => {
          const done = !!stepStatus?.[s.key];
          const active = idx === currentIndex;
          return (
            <div
              key={s.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: done ? "#16a34a" : active ? "#1c1917" : "#a8a29e",
                fontWeight: active ? 600 : 400,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: done ? "#16a34a" : active ? "#1c1917" : "#e7e5e4",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {done ? "✓" : idx + 1}
              </span>
              {s.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WizardFooter({ currentIndex, totalSteps, isLast, onBack, onNext }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        borderTop: "1px solid #e7e5e4",
        background: "#fff",
        position: "sticky",
        bottom: 0,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={currentIndex === 0}
        style={{
          padding: "10px 20px",
          fontSize: 14,
          fontWeight: 600,
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: "#fff",
          color: currentIndex === 0 ? "#a8a29e" : "#374151",
          cursor: currentIndex === 0 ? "not-allowed" : "pointer",
        }}
      >
        ← Tillbaka
      </button>
      <button
        type="button"
        onClick={onNext}
        style={{
          padding: "10px 24px",
          fontSize: 14,
          fontWeight: 600,
          borderRadius: 8,
          border: "none",
          background: "#1c1917",
          color: "#fff",
          cursor: "pointer",
        }}
      >
        {isLast ? "Slutför" : "Nästa →"}
      </button>
    </div>
  );
}

function useSellerCtx() {
  return useOutletContext();
}

function NoAccessMessage({ children }) {
  return <p className="sp-empty">{children}</p>;
}

// ─── Valuation read-only module ─────────────────────────────────────────────

function formatNumber(n, currency) {
  if (n == null || isNaN(Number(n))) return "—";
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: currency || "SEK",
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return Number(n).toLocaleString("sv-SE");
  }
}

function ValuationModule({ workspaceKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiFetch(`/users/seller-portal/${workspaceKey}/valuation/`)
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) throw new Error(d.detail || "Kunde inte ladda värdering.");
        setData(d);
      })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workspaceKey]);

  if (loading) {
    return (
      <section className="sp-module">
        <p style={{ color: "#9ca3af" }}>Laddar värdering…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section className="sp-module">
        <p className="sp-error">{error}</p>
      </section>
    );
  }
  if (!data?.exists) {
    return (
      <section className="sp-module">
        <h2 className="sp-module-title">Värdering</h2>
        <p className="sp-module-desc">
          Din mäklare har ännu inte slutfört en värdering. När den är klar visas
          intervallet här.
        </p>
      </section>
    );
  }

  const cur = data.currency || "SEK";
  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Värdering</h2>
      <p className="sp-module-desc">
        Indikativt intervall som mäklaren tagit fram. Detta är inte en bindande
        värdering utan ett underlag inför diskussion.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 16 }}>
        {[
          { label: "Lågt", v: data.value_low },
          { label: "Bas", v: data.value_mid },
          { label: "Högt", v: data.value_high },
        ].map((c) => (
          <div key={c.label} style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>{c.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginTop: 4 }}>{formatNumber(c.v, cur)}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 18, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
        <div><strong>Resultatmått:</strong> {data.metric_used || "—"} {data.metric_value != null ? `(${formatNumber(data.metric_value, cur)})` : ""}</div>
        {data.branch_label && <div><strong>Bransch:</strong> {data.branch_label}</div>}
        {data.finalized_at && <div><strong>Slutförd:</strong> {new Date(data.finalized_at).toLocaleDateString("sv-SE")}</div>}
        {data.equity_value_mid != null && (
          <div style={{ marginTop: 6 }}>
            <strong>Aktievärde (bas):</strong> {formatNumber(data.equity_value_mid, cur)}
          </div>
        )}
      </div>
    </section>
  );
}

export function SellerValuationPage() {
  const { workspaceKey } = useSellerCtx();
  return <ValuationModule workspaceKey={workspaceKey} />;
}

export function SellerDataroomPage() {
  return (
    <section className="sp-module" style={{ padding: 0 }}>
      <DataRoom viewerRole="seller" chromeless />
    </section>
  );
}

export function SellerQAPage() {
  const { workspaceKey } = useSellerCtx();
  return <QuestionsPanel slug={workspaceKey} viewerRole="seller" embedded />;
}

export function SellerFinancialsPage() {
  const { workspaceKey, info } = useSellerCtx();
  if (!info?.modules?.includes("financials")) {
    return <NoAccessMessage>Du har inte åtkomst till finansiell data.</NoAccessMessage>;
  }
  return <FinancialsModule workspaceKey={workspaceKey} />;
}

export function SellerDDPage() {
  const { workspaceKey, info } = useSellerCtx();
  if (!info?.modules?.includes("dd")) {
    return <NoAccessMessage>Du har inte åtkomst till Due diligence.</NoAccessMessage>;
  }
  return <DDModule workspaceKey={workspaceKey} />;
}

export function SellerCompanyDetailsPage() {
  const { workspaceKey, info, sellerCompanyName } = useSellerCtx();
  if (!info?.modules?.includes("company_details")) {
    return <NoAccessMessage>Du har inte åtkomst till bolagsinfo.</NoAccessMessage>;
  }
  return <CompanyDetailsModule workspaceKey={workspaceKey} sellerCompanyName={sellerCompanyName} />;
}

// Read-only IM view for the seller. Reuses the broker-finalized IM
// document — no editor surface, no PM Studio access.
function SellerIMModule({ workspaceKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    apiFetch(`/users/seller-portal/${workspaceKey}/im/`)
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "Kunde inte ladda IM:et.");
        }
        return res.json();
      })
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workspaceKey]);

  if (loading) {
    return (
      <section className="sp-module">
        <h2 className="sp-module-title">Informationsmemorandum</h2>
        <p style={{ color: "#9ca3af", fontSize: 13 }}>Laddar…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="sp-module">
        <h2 className="sp-module-title">Informationsmemorandum</h2>
        <p className="sp-error">{error}</p>
      </section>
    );
  }

  if (!data?.finalized) {
    return (
      <section className="sp-module">
        <h2 className="sp-module-title">Informationsmemorandum</h2>
        <p style={{ color: "#6b7280", fontSize: 13 }}>
          Mäklaren har inte färdigställt IM:et ännu. Du får tillgång så
          fort det är klart att skickas ut.
        </p>
      </section>
    );
  }

  return (
    <section className="sp-module">
      <h2 className="sp-module-title">Informationsmemorandum</h2>
      <p className="sp-module-desc">
        Detta är den färdigställda versionen som mäklaren skickar till
        intresserade köpare.
      </p>

      {data.url && (
        <div style={{ marginBottom: 16 }}>
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              padding: "9px 18px",
              background: "#111827",
              color: "#fff",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Öppna som PDF ↗
          </a>
        </div>
      )}

      {Array.isArray(data.sections) && data.sections.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {data.sections.map((section) => (
            <div
              key={section.key}
              style={{
                padding: "18px 22px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fafafa",
              }}
            >
              <div style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 10,
                color: "#111827",
              }}>
                {section.title}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#374151",
                  lineHeight: 1.55,
                }}
                // Sections are broker-authored HTML from the IM editor.
                dangerouslySetInnerHTML={{ __html: section.content || "" }}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function SellerIMPage() {
  const { workspaceKey } = useSellerCtx();
  return <SellerIMModule workspaceKey={workspaceKey} />;
}
